import { UpdateCalendarEventInput } from '@griply/types';
import { ExternalEventPatch, patchExternalEventInWindow } from '@griply/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateExternalCalendarEvent } from 'shared/services/backend-api';
import { ExternalCalendarEvent } from 'shared/types/external-calendar-event';

import { queryKey as eventQueryKey } from './use-external-calendar-event-query';
import { queryKey as eventsQueryKey } from './use-external-calendar-events-query';

export type UpdateExternalCalendarEventVariables = {
  payload: UpdateCalendarEventInput;
  // The grid values to paint immediately — boundaries for a drag, boundaries + title for a
  // form save. These callables have no snapshot listener behind them (the "no optimistic
  // updates" rule in features-and-hooks.md is about Firestore subscriptions), so without
  // this the event keeps its old slot and title until the round-trip and refetch land.
  optimistic?: ExternalEventPatch | null;
};

type CachedEvents = ExternalCalendarEvent[] | undefined;

export const useUpdateExternalCalendarEventMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ payload }: UpdateExternalCalendarEventVariables) =>
      updateExternalCalendarEvent(payload),

    onMutate: async ({ payload, optimistic }) => {
      if (!optimistic) {
        return null;
      }

      // every cached window holding this event, so the patch survives a view switch
      const previous = client.getQueriesData<ExternalCalendarEvent[]>({
        queryKey: [eventsQueryKey],
      });

      client.setQueriesData(
        { queryKey: [eventsQueryKey] },
        (events: CachedEvents) =>
          patchExternalEventInWindow(events, payload.eventId, optimistic),
      );

      return { previous };
    },

    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, events]) =>
        client.setQueryData<CachedEvents>(key, events),
      );
    },

    onSettled: async () => {
      await client.invalidateQueries({ queryKey: [eventsQueryKey] });
      await client.invalidateQueries({ queryKey: [eventQueryKey] });
    },
  });
};
